import { useState, useRef, useEffect } from 'react';
import { Menu, Bell, Search, ChevronDown, LogOut, User, Settings, X, CheckCheck } from 'lucide-react';
import { useUIStore } from '../../store/uiStore';
import { useNotificationStore } from '../../store/notificationStore';
import { useAuthStore } from '../../store/authStore';
import { cn, formatRelative } from '../../lib/utils';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { logoutAndCleanup } from '../../lib/session';
import { notificationApi } from '../../features/notifications/api/notifications.api';

interface Notification {
  _id: string;
  title: string;
  body: string;
  read: boolean;
  type: string;
  actionUrl?: string;
  createdAt: string;
}

function cleanNotificationTitle(title: string) {
  return title
    .replace(/^[\s\p{Extended_Pictographic}\uFE0F\u200D]+/u, '')
    .trim();
}

export default function TopBar() {
  const { toggleMobileSidebar } = useUIStore();
  const { unreadCount, markRead, markAllRead, setNotifications } = useNotificationStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setShowSearch(false); }, [location.pathname, location.search]);
  useEffect(() => {
    const media = window.matchMedia?.('(min-width: 640px)');
    const closeOnDesktop = () => { if (media?.matches) setShowSearch(false); };
    media?.addEventListener('change', closeOnDesktop);
    return () => media?.removeEventListener('change', closeOnDesktop);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearch(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: notificationsResponse, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications-dropdown'],
    queryFn: () => notificationApi.list({ limit: 10, _ts: Date.now() }),
    select: (r: any) => r as { data: Notification[]; meta?: { unreadCount?: number } },
    refetchInterval: 10000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  useEffect(() => {
    const notificationsData = notificationsResponse?.data;
    if (!notificationsData) return;
    setNotifications(notificationsData.map((notification) => ({
      id: notification._id,
      type: notification.type,
      title: notification.title,
      body: notification.body,
      read: notification.read,
      actionUrl: notification.actionUrl,
      createdAt: notification.createdAt,
    })), notificationsResponse?.meta?.unreadCount);
  }, [notificationsResponse, setNotifications]);

  const markReadMutation = useMutation({
    mutationFn: notificationApi.markRead,
    onSuccess: (_, id) => {
      markRead(id);
      qc.invalidateQueries({ queryKey: ['notifications-dropdown'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationApi.markAllReadPost,
    onSuccess: () => {
      markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications-dropdown'] });
      toast.success('All notifications marked as read');
    },
  });


  const handleLogout = () => logoutAndCleanup(navigate);

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSearch(false);
      searchTriggerRef.current?.focus();
      return;
    }
    if (e.key === 'Enter' && search.trim()) {
      navigate(`/leads?search=${encodeURIComponent(search)}`);
      setSearch('');
      setShowSearch(false);
    }
  };

  const notifications = notificationsResponse?.data ?? [];

  const notifTypeColors: Record<string, string> = {
    tender_alert: 'bg-gold/20 text-gold',
    lead_update: 'bg-blue-100 text-blue-600',
    agent_complete: 'bg-emerald-100 text-emerald-600',
    meeting_reminder: 'bg-purple-100 text-purple-600',
    system: 'bg-gray-100 text-gray-600',
  };

  return (
    <header
      className="sticky top-0 z-20 w-full h-header bg-white border-b border-gray-100 flex items-center px-4 sm:px-6 gap-3"
      style={{ boxShadow: '0 1px 8px color-mix(in srgb, var(--color-navy) 6%, transparent)' }}
    >
      {/* Mobile menu trigger */}
      <button
        onClick={toggleMobileSidebar}
        className="lg:hidden p-2 rounded-lum text-gray-500 hover:bg-gray-100 transition-colors"
      >
        <Menu size={20} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="relative hidden sm:block w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="search" aria-label="Search workspace" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch}
            placeholder="Search leads, tenders, meetings... (Enter)" className="lum-input lum-input-search py-2 text-sm" />
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2">
      <div ref={searchRef} className="shrink-0">
        <button ref={searchTriggerRef} type="button" aria-label={showSearch ? 'Close search' : 'Open search'} aria-expanded={showSearch} aria-controls="mobile-header-search"
          className="sm:hidden p-2 rounded-lum text-navy hover:bg-gray-100 transition-colors"
          onClick={() => { setShowSearch(value => !value); setShowNotifications(false); setShowProfile(false); }}>
          <Search size={20} />
        </button>
        {showSearch && <div id="mobile-header-search" role="search" aria-label="Mobile workspace search" className="absolute top-full inset-x-0 border-b border-gray-100 bg-white p-3 shadow-sm sm:hidden">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input autoFocus type="search" aria-label="Search workspace" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch}
              placeholder="Search leads... (Enter)" className="lum-input lum-input-search pr-10 py-2 text-sm" />
            <button type="button" aria-label="Close mobile search" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-secondary"
              onClick={() => { setShowSearch(false); searchTriggerRef.current?.focus(); }}><X size={16} /></button>
          </div>
        </div>}
      </div>
        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button
            className="relative p-2 rounded-lum text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowProfile(false);
              refetchNotifications();
            }}
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notification-dot">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-navy" />
                  <h3 className="font-semibold text-navy text-sm">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="bg-gold text-white text-xs font-bold rounded-full px-2 py-0.5">{unreadCount}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllReadMutation.mutate()}
                      className="text-xs text-gold hover:text-navy transition-colors flex items-center gap-1"
                      title="Mark all as read"
                    >
                      <CheckCheck size={14} /> All read
                    </button>
                  )}
                  <button onClick={() => setShowNotifications(false)} className="p-1 hover:bg-gray-200 rounded transition-colors">
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Notification list */}
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                {notifications.length === 0 ? (
                  <div className="py-10 text-center text-secondary text-sm">
                    <Bell size={28} className="text-gray-200 mx-auto mb-2" />
                    No notifications yet
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n._id}
                      className={cn(
                        'flex gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors',
                        !n.read && 'bg-blue-50/30'
                      )}
                      onClick={() => {
                        if (!n.read) markReadMutation.mutate(n._id);
                        if (n.actionUrl) navigate(n.actionUrl);
                        setShowNotifications(false);
                      }}
                    >
                      <div className={cn(
                        'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                        !n.read ? 'bg-blue-500' : 'bg-gray-200'
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn('text-sm font-medium', !n.read ? 'text-navy' : 'text-gray-600')}>
                            {cleanNotificationTitle(n.title)}
                          </p>
                          <span className="text-xs text-secondary flex-shrink-0">
                            {formatRelative(n.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs text-secondary mt-0.5 line-clamp-2">{n.body}</p>
                        <span className={cn('inline-block text-xs px-2 py-0.5 rounded-full mt-1', notifTypeColors[n.type] || notifTypeColors.system)}>
                          {(n.type || 'system').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                <Link
                  to="/notifications"
                  onClick={() => setShowNotifications(false)}
                  className="text-xs text-gold hover:text-navy transition-colors flex items-center gap-1 justify-center"
                >
                  View all notifications
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* User avatar / Profile Menu */}
        {user && (
          <div ref={profileRef} className="relative">
            <button
              onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
              className="flex items-center gap-2 cursor-pointer group p-1.5 rounded-lum hover:bg-gray-50 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center text-white text-xs font-bold">
                {user.profile.firstName[0]}{user.profile.lastName[0]}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {user.profile.firstName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user.role.replace(/_/g, ' ')}</p>
              </div>
              <ChevronDown size={14} className={cn('text-gray-400 transition-transform', showProfile && 'rotate-180')} />
            </button>

            {showProfile && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                {/* User info header */}
                <div className="px-4 py-3 bg-navy text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center text-navy font-bold text-sm">
                      {user.profile.firstName[0]}{user.profile.lastName[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{user.profile.firstName} {user.profile.lastName}</p>
                      <p className="text-xs text-white/70 capitalize">{user.role.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-white/50 truncate">{user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <Link
                    to="/settings/profile"
                    onClick={() => setShowProfile(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User size={16} className="text-gray-400" />
                    My Profile
                  </Link>
                  <Link
                    to="/settings/organization"
                    onClick={() => setShowProfile(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings size={16} className="text-gray-400" />
                    Organisation Settings
                  </Link>
                  {(user.role === 'super_admin') && (
                    <Link
                      to="/admin/users"
                      onClick={() => setShowProfile(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <User size={16} className="text-gray-400" />
                      Manage Users
                    </Link>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
